import type { SubmitArgs } from '@/context/JobCenterContext'

/**
 * 用户在报价确认弹窗里改过的提示词。
 *
 * `undefined`（而不是空对象）= 一个字都没改，提交**照原样走 `args`**。
 * 多这一层判断不是省事，是为了让「没改」这件事只表达一次：拿一份逐字相同的副本
 * 去覆盖 `args`，哪天两边算得不一样（比如改完之后参数又变了、`args` 重算过），
 * 覆盖就是**静默用旧值**——而这条正是「预览 == 提交原文」最该守住的地方。
 *
 * 两个字段对应弹窗里的两支：有 `shotPrompts` 的档只认 `shots`，没有的档只认 `base`。
 * 刻意不做成一个 `string[]` —— 下标在两种形态下含义不同（一个是「第几张」、
 * 一个是「兜底那份」），合成一个数组就等于让调用方去猜这一次是哪种。
 */
export type PromptEdits = {
  /** 逐张提示词，顺序与 `info.shotPrompts` **一一对应** */
  shots?: string[]
  /** 顶层 `prompt`（没有逐张词的那一档） */
  base?: string
}

/**
 * 把弹窗里改过的提示词并回提交参数。
 *
 * ## 为什么是一个独立的纯模块
 *
 * 2026-10-08 抽出来。它此前是 `useGenerateFlow.ts` 里的模块私有函数 ——
 * 而「用户改的那一份**到底有没有发出去**」这件事，靠 grep 源码是证不了的：
 * grep 只能说明「有这么一行字」，说不出「改完之后载荷里的第 1 张确实是新那段」。
 * 抽成不依赖 React 的纯函数之后，冒烟脚本可以直接 import 它**真跑一遍**
 * （见 `scripts/smoke-detail-specs.ts` 的「编辑值并回载荷」那一组）。
 *
 * ⚠️ 逐张那一支要**同时**改 `prompts` 与 `prompt`：后端按 `prompts[i]` 逐张分发
 * （缺位才回退 `prompt`），而 `prompt` 是「这一批的兜底 / 第一张」。
 * 只改一个的话，第 1 张与其余张会指向两份不同的词 —— 而出图不报错。
 *
 * 返回**新对象**，不改原 `args`：原对象还挂在别处被读着（页面里 `buildArgs`
 * 的返回值），就地改会让「提交前一刻的参数」与「提交后页面上的参数」
 * 变成同一个引用。
 */
export function withPromptEdits(a: SubmitArgs, e?: PromptEdits): SubmitArgs {
  if (!e) return a
  if (e.shots?.length) {
    return { ...a, prompts: e.shots, prompt: e.shots[0] ?? a.prompt }
  }
  if (e.base !== undefined && e.base !== a.prompt) {
    /**
     * 🔴 有 `promptInputKey` 的档，**必须连 `inputs` 里那一格一起改**。
     *
     * 那些档的真输入不在 `prompt` 上 —— 数字人念的是 `inputs.script`、
     * 语音合成读的是 `inputs.text`，`prompt` 只是给人看的模板。
     * 只改 `prompt` 的后果很具体：用户在弹窗里把台词改了两句、确认，
     * 送进 TTS 的还是原来那段 —— 任务 succeeded、钱照扣、成片念旧台词，
     * 而界面上与任务记录里写的都是新台词。**只有听成片才发现**，
     * 这正是「预览 == 提交原文」那条铁律要防的东西。
     *
     * ⚠️ 不声明 `promptInputKey` 的档**原样返回 `a.inputs` 这个引用** ——
     * 它们改的本来就是真输入，顺手重建一份 inputs 只会让下游误以为输入变了。
     */
    return a.promptInputKey
      ? {
          ...a,
          prompt: e.base,
          inputs: { ...a.inputs, [a.promptInputKey]: e.base },
        }
      : { ...a, prompt: e.base }
  }
  return a
}
